'use client';

import { ArrowBigDownDashIcon, Loader2Icon, RotateCwIcon } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import type { SdkCompatibilityReason } from '@/app/editor/hooks/useInitApp';

import { requestInstallUpdate, requestManualUpdateCheck } from '@/app/editor/hooks/useInitApp';
import { globalStore } from '@/app/globalStore';
import { sdk } from '@/app/sdk';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { SDK_RELEASE_URL } from '@/const';

/** Interval between automatic retries while the updater is in the error phase. */
const WAITING_DIALOG_ERROR_RETRY_START_DELAY_MS = 3_000;
const WAITING_DIALOG_ERROR_RETRY_INTERVAL_MS = 5_000;

type SdkUpdateWaitingDialogProps = {
  isOpen: boolean;
  currentVersion: string;
  requiredVersion: string;
  currentEngineVersion?: string;
  requiredEngineVersion?: string;
  reason?: SdkCompatibilityReason;
};

const isEngineCompatibilityReason = (reason: SdkCompatibilityReason | undefined): boolean => (
  reason === 'engineVersionTooOld' || reason === 'engineVersionUnknown'
);

const getDialogTitle = (): string => {
  if (globalStore.updaterPhase === 'readyToInstall') {
    return 'App Update Ready To Restart';
  }
  if (globalStore.updaterPhase === 'error') {
    return 'App Update Requires Attention';
  }
  return 'App Update Required Before Continuing';
};

const getPhaseDescription = (): string => {
  if (globalStore.updaterPhase === 'checking') {
    return 'Checking for a compatible App (SDK) update...';
  }
  if (globalStore.updaterPhase === 'available') {
    if (globalStore.updaterProgressPercent === null) {
      return 'Update found. Connecting to download — please keep this window open.';
    }
    return 'Update found. Downloading in the background.';
  }
  if (globalStore.updaterPhase === 'downloading') {
    return 'Downloading App (SDK) update. Please keep this window open.';
  }
  if (globalStore.updaterPhase === 'readyToInstall') {
    return 'Update downloaded. Restart now to continue.';
  }
  if (globalStore.updaterPhase === 'error') {
    return globalStore.updaterErrorMessage ?? 'The updater encountered an error.';
  }
  return 'Preparing update checks...';
};

const getStatusMeta = (): string => {
  const meta: string[] = [];
  const elapsed = globalStore.updaterElapsedSeconds;
  if (elapsed > 0) {
    meta.push(`Working for ${elapsed}s`);
  }
  if (globalStore.updaterRetryAttempt > 0) {
    meta.push(`Retry attempt ${globalStore.updaterRetryAttempt}`);
  }
  return meta.join(' • ');
};

const getPhaseIcon = () => {
  if (globalStore.updaterPhase === 'checking') {
    return <Loader2Icon className="size-4 animate-spin" />;
  }
  if (globalStore.updaterPhase === 'available' && globalStore.updaterProgressPercent === null) {
    return <Loader2Icon className="size-4 animate-spin" />;
  }
  if (globalStore.updaterPhase === 'readyToInstall') {
    return <RotateCwIcon className="size-4" />;
  }
  return <ArrowBigDownDashIcon className="size-4" />;
};

export const SdkUpdateWaitingDialog = observer(({
  isOpen,
  currentVersion,
  requiredVersion,
  currentEngineVersion,
  requiredEngineVersion,
  reason
}: SdkUpdateWaitingDialogProps) => {
  const isEngineFailure = isEngineCompatibilityReason(reason);
  const isRetryInFlightRef = useRef(false);
  const progressValue = globalStore.updaterProgressPercent === null
    ? undefined
    : Math.max(0, Math.min(100, globalStore.updaterProgressPercent));

  const updaterPhase = globalStore.updaterPhase;
  const isUpdaterMockEnabled = globalStore.isUpdaterMockEnabled;
  const statusMeta = getStatusMeta();
  const showProgressSection = updaterPhase !== 'readyToInstall';

  useEffect(() => {
    if (!isOpen || isUpdaterMockEnabled || updaterPhase !== 'idle') {
      return;
    }
    void requestManualUpdateCheck();
  }, [isOpen, isUpdaterMockEnabled, updaterPhase]);

  useEffect(() => {
    if (!isOpen || isUpdaterMockEnabled || updaterPhase !== 'error') {
      return;
    }

    const runRetryCheck = () => {
      if (isRetryInFlightRef.current) {
        return;
      }
      isRetryInFlightRef.current = true;
      void requestManualUpdateCheck().finally(() => {
        isRetryInFlightRef.current = false;
      });
    };

    const startupRetryTimeout = window.setTimeout(() => {
      runRetryCheck();
    }, WAITING_DIALOG_ERROR_RETRY_START_DELAY_MS);

    const intervalId = window.setInterval(runRetryCheck, WAITING_DIALOG_ERROR_RETRY_INTERVAL_MS);

    return () => {
      window.clearTimeout(startupRetryTimeout);
      window.clearInterval(intervalId);
      isRetryInFlightRef.current = false;
    };
  }, [isOpen, isUpdaterMockEnabled, updaterPhase]);

  return (
    <Dialog open={isOpen} modal={false}>
      <DialogPortal>
        <div className="fixed top-(--titlebar-height) left-0 right-0 bottom-0 z-40 bg-[#1A1B1E]/70 backdrop-blur-xs" />
      </DialogPortal>
      <DialogContent
        className="sm:max-w-lg"
        showCloseButton={false}
        overlayExcludeTitleBar={true}
        preventInteractOutside={true}
      >
        <DialogHeader>
          <DialogTitle className="text-primary text-xl flex items-center gap-2">
            {getPhaseIcon()}
            {getDialogTitle()}
          </DialogTitle>
          <DialogDescription asChild className="space-y-3 text-sm">
            <div>
              <div>
                {isEngineFailure ? (
                  <>
                    Your App (SDK) engine version <strong>{currentEngineVersion || 'unknown'}</strong> is below the required engine version <strong className="text-primary">{requiredEngineVersion || 'unknown'}</strong> (major.minor).
                  </>
                ) : (
                  <>
                    Your App (SDK) version <strong>{currentVersion}</strong> is below the required App (SDK) version <strong className="text-primary">{requiredVersion}</strong>.
                  </>
                )}
              </div>
              <div>{getPhaseDescription()}</div>
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {showProgressSection ? (
            <>
              {progressValue === undefined ? (
                <div className="h-2 w-full rounded-full bg-primary/30 animate-pulse" />
              ) : (
                <Progress value={progressValue} />
              )}
              <div className="flex items-center justify-between">
                <span className="text-2xs text-muted-foreground">
                  {progressValue === undefined
                    ? (updaterPhase === 'available' ? 'Connecting to download...' : 'Waiting for progress...')
                    : `${Math.round(progressValue)}% downloaded`}
                </span>
                {globalStore.updaterTargetVersion && (
                  <span className="text-2xs text-muted-foreground ml-3">
                    Target App (SDK) version: v{globalStore.updaterTargetVersion}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-md border border-blue-500/40 bg-blue-500/10 px-3 py-2 text-xs text-blue-200">
              Download complete. Restart now to install the update.
            </div>

          )}
          {statusMeta && (
            <div className="text-2xs text-muted-foreground">
              {statusMeta}
            </div>
          )}
          {updaterPhase === 'error' && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {globalStore.updaterErrorMessage ?? 'The updater encountered an error.'}
            </div>
          )}
        </div>

        <DialogFooter className="sm:justify-center gap-2 pt-4">
          <div className="flex flex-col sm:flex-row gap-2 w-full">
            <Button
              variant="outline"
              onClick={() => {
                void sdk?.os.openUrl(SDK_RELEASE_URL);
              }}
              className="flex-1 min-w-[140px]"
            >
              Download manually
            </Button>
            {(globalStore.updaterPhase === 'checking'
              || globalStore.updaterPhase === 'available'
              || globalStore.updaterPhase === 'downloading'
              || globalStore.updaterPhase === 'error') && (
              <Button
                variant="outline"
                onClick={() => {
                  void requestManualUpdateCheck();
                }}
                className="flex-1 min-w-[140px]"
              >
                  Retry Check
              </Button>
            )}
            {globalStore.updaterPhase === 'readyToInstall' && (
              <Button
                variant="primary"
                onClick={() => {
                  void requestInstallUpdate();
                }}
                className="flex-1 min-w-[140px] animate-pulse"
              >
                Restart now
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
});

SdkUpdateWaitingDialog.displayName = 'SdkUpdateWaitingDialog';
